import { getNlsString } from '../nls/nlsRuntime'
import type { NlsInterpolationValues } from '../nls/nlsTypes'
import type { NlsKey } from '../nls/strings_nls'
import type { AiMagicChatTurn } from '../services/aiService'
import type { ActionPayload } from '../types/ai'

export type AiMagicNlsT = (key: NlsKey, values?: NlsInterpolationValues) => string

const defaultAiMagicT: AiMagicNlsT = (key, values) => getNlsString(key, values)

export const getLineStatusLabel = (
  status: NonNullable<AiMagicChatTurn['status']>,
  t: AiMagicNlsT = defaultAiMagicT,
): string => {
  if (status === 'CONFIRMED') return t('NLSAiStatusConfirmed')
  if (status === 'PENDING_APPROVAL') return t('NLSAiStatusPendingApproval')
  if (status === 'PENDING_CLARIFICATION') return t('NLSAiStatusPendingClarification')
  return t('NLSAiStatusRejected')
}

export const getLineQuickActionApplyLabel = (
  actions: ActionPayload[],
  t: AiMagicNlsT = defaultAiMagicT,
): string => {
  if (actions.some((action) => action.type === 'DEPENDENCY_UPDATE')) return t('NLSAiActionReviewDependencies')
  if (actions.some((action) => action.type === 'DELTA_PATCH')) {
    return t(actions.length > 1 ? 'NLSAiActionReviewPatches' : 'NLSAiActionReviewPatch')
  }
  return t('NLSAiActionMoveToManage')
}

export const getAiChatHistoryCopy = (t: AiMagicNlsT = defaultAiMagicT) => ({
  ariaHistory: t('NLSAiAriaChatHistory'),
  newChat: t('NLSAiActionNewChat'),
  rename: t('NLSAiActionRename'),
  delete: t('NLSAiActionDelete'),
  actionsFor: (label: string) => t('NLSAiAriaActionsFor', { label }),
})

export const getAiComposerCopy = (t: AiMagicNlsT = defaultAiMagicT) => ({
  addContext: t('NLSAiComposerAddContext'),
  menuOptions: t('NLSAiComposerOptions'),
  contextCount: (count: number, limit: string) => t('NLSAiComposerContextCount', { count, limit }),
  addToContext: t('NLSAiComposerAddToContext'),
  back: t('NLSAiComposerBack'),
  addPhotosFiles: t('NLSAiComposerAddPhotosFiles'),
  addPhotosFilesHint: t('NLSAiComposerAddPhotosFilesHint'),
  attachPublicLink: t('NLSAiComposerAttachPublicLink'),
  attachPublicLinkHint: t('NLSAiComposerAttachPublicLinkHint'),
  useAppAsset: t('NLSAiComposerUseAppAsset'),
  useAppAssetHint: t('NLSAiComposerUseAppAssetHint'),
  selectTimelineItems: t('NLSAiComposerSelectTimelineItems'),
  selectedCount: (count: number) => t('NLSAiComposerSelectedCount', { count }),
  operatingMode: t('NLSAiComposerOperatingMode'),
  planMode: t('NLSAiComposerPlanMode'),
  planModeHint: t('NLSAiComposerPlanModeHint'),
  pastePublicLink: t('NLSAiComposerPastePublicLink'),
  checking: t('NLSAiComposerChecking'),
  lineCanReadLink: t('NLSAiComposerLineCanReadLink'),
  linkValidationPending: t('NLSAiComposerLinkValidationPending'),
  lineCouldNotReachLink: t('NLSAiComposerLineCouldNotReachLink'),
  add: t('NLSAiActionAdd'),
  attachContext: t('NLSAiComposerAttachContext'),
  attachContextHint: t('NLSAiComposerAttachContextHint'),
})

export const getAiGoalTreeCopy = (t: AiMagicNlsT = defaultAiMagicT) => ({
  noGoals: t('NLSAiGoalTreeNoGoals'),
  collapseGoal: t('NLSAiActionCollapseGoal'),
  expandGoal: t('NLSAiActionExpandGoal'),
  collapseTask: t('NLSAiActionCollapseTask'),
  expandTask: t('NLSAiActionExpandTask'),
  collapseSubtask: t('NLSAiActionCollapseSubtask'),
  expandSubtask: t('NLSAiActionExpandSubtask'),
  noChecklistItems: t('NLSAiGoalTreeNoChecklistItems'),
})

export const getAiEditSelectorCopy = (t: AiMagicNlsT = defaultAiMagicT) => ({
  ariaEditExisting: t('NLSAiEditExistingAria'),
  titleEditExisting: t('NLSAiEditExistingTitle'),
  menuLabel: t('NLSAiEditExistingMenuLabel'),
  summary: (count: number) => t('NLSAiEditExistingSummary', { count }),
})
